import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Layout, Typography, Avatar, Dropdown, Button, Modal, Badge, Breadcrumb, Divider, ConfigProvider, Popover, Alert, Space } from 'antd';
import { UserOutlined, SettingOutlined, LogoutOutlined, BellOutlined, CarOutlined, AppstoreOutlined, DownOutlined, MenuUnfoldOutlined, MenuFoldOutlined, SyncOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { logoutUser } from '../../slices/authSlice';
import NotificationPanel from '../notifications/NotificationPanel';
import { useNotifications } from '../../context/NotificationContext';
import websocketService from '../../services/websocketService';

const { Header } = Layout;
const { Text } = Typography;

// Add collapsed, setCollapsed, and isMobile to props
const AppHeader = ({ getPageTitle, currentPage, collapsed, setCollapsed, isMobile }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.auth);
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const notifications = useNotifications();
  const unreadCount = notifications?.unreadCount || 0;

  // WebSocket connection status for AFK handling
  const [wsDisconnected, setWsDisconnected] = useState(false);
  const [wsReconnecting, setWsReconnecting] = useState(false);

  // Track WebSocket connection status globally
  useEffect(() => {
    const handleConnected = () => {
      setWsDisconnected(false);
      setWsReconnecting(false);
    };

    const handleDisconnected = () => {
      setWsDisconnected(true);
      setWsReconnecting(true);
    };

    const handleMaxReconnectAttempts = () => {
      setWsDisconnected(true);
      setWsReconnecting(false);
    };

    const handleConnectionError = () => {
      setWsDisconnected(true);
      setWsReconnecting(true);
    };

    // Subscribe to WebSocket events
    websocketService.on('connected', handleConnected);
    websocketService.on('disconnected', handleDisconnected);
    websocketService.on('connection_error', handleConnectionError);
    websocketService.on('max_reconnect_attempts', handleMaxReconnectAttempts);

    return () => {
      websocketService.off('connected', handleConnected);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.off('connection_error', handleConnectionError);
      websocketService.off('max_reconnect_attempts', handleMaxReconnectAttempts);
    };
  }, []);

  const handleRetryConnection = () => {
    setWsDisconnected(true);
    setWsReconnecting(true);
    websocketService.forceReconnect();
  };

  // If auth is not initialized yet, return a loading state
  if (loading) {
    return <Header className="px-6" style={{ backgroundColor: '#f5f7fb' }} />;
  }

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const showLogoutConfirmation = () => {
    setIsLogoutModalVisible(true);
  };

  const handleLogoutCancel = () => {
    setIsLogoutModalVisible(false);
  };

  const handleLogoutConfirm = () => {
    handleLogout();
    setIsLogoutModalVisible(false);
  };

  // Define user menu items
  const userItems = [
    {
      key: 'settings',
      label: 'Account Settings',
      icon: <SettingOutlined />,
      onClick: () => navigate('/settings')
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: showLogoutConfirmation
    }
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 6,
        },
      }}
    >
      {/* WebSocket disconnected warning - shown on all pages */}
      {wsDisconnected && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 sticky top-0 z-50">
          <Space>
            <ExclamationCircleOutlined className="text-amber-600" />
            <span className="text-amber-700 text-sm">
              {wsReconnecting ? 'Reconnecting to real-time service...' : 'Real-time updates paused.'}
            </span>
            {!wsReconnecting && (
              <Button
                type="link"
                size="small"
                icon={<SyncOutlined />}
                onClick={handleRetryConnection}
                className="text-blue-600"
              >
                Retry Connection
              </Button>
            )}
          </Space>
        </div>
      )}

      <Header
        className="px-3 md:px-4 flex items-center justify-between sticky top-0 z-10 shadow-sm border-b border-gray-200"
        style={{ backgroundColor: '#f5f7fb' }}
      >
        {/* Left section with logo, toggle button, and breadcrumb */}
        <div className="flex items-center min-w-0 flex-1">
          {/* Sidebar toggle button */}
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="text-base w-8 h-8 md:w-10 md:h-10 mr-2 md:mr-3 flex-shrink-0"
          />
          
          {/* Logo - hidden on very small screens */}
          <div className="hidden sm:flex items-center mr-3 md:mr-6 flex-shrink-0">
            <Avatar
              size={30}
              className="bg-blue-500 flex items-center justify-center"
              icon={<CarOutlined className="text-base" />}
            />
          </div>

          {/* Breadcrumb - responsive */}
          <div 
            className="hidden md:block"
            style={{ backgroundColor: '#f5f7fb', padding: '4px 8px', borderRadius: '6px' }}
          >
            <Breadcrumb
              items={[
                {
                  title: (
                    <div className="flex items-center gap-1">
                      <AppstoreOutlined className="text-sm" />
                      <span>GIS E-Invoice</span>
                    </div>
                  ),
                },
                {
                  title: getPageTitle(),
                },
              ]}
              separator=">"
              className="text-sm"
              style={{ backgroundColor: 'transparent' }}
            />
          </div>

          {/* Mobile: Show only current page title */}
          <div className="md:hidden min-w-0 flex-1">
            <Text 
              strong 
              className="text-gray-800 text-sm truncate block"
              style={{ maxWidth: '100%' }}
            >
              {getPageTitle()}
            </Text>
          </div>
        </div>

        {/* Right section with notifications and user menu */}
        <div className="flex items-center flex-shrink-0 ml-2">
          <Popover
            content={<NotificationPanel onClose={() => setNotificationPanelOpen(false)} />}
            trigger="click"
            placement={isMobile ? "bottom" : "bottomRight"}
            open={notificationPanelOpen}
            onOpenChange={setNotificationPanelOpen}
            overlayClassName="notification-dropdown"
            align={isMobile ? { offset: [0, -8] } : { offset: [0, -16] }}
            overlayStyle={isMobile ? { width: 'calc(100vw - 32px)', left: '16px !important' } : {}}
          >
            <div className="p-2 rounded-full cursor-pointer transition-all duration-300">
              <Badge count={unreadCount} offset={[-2, 2]}>
                <BellOutlined className="text-xl text-gray-600" />
              </Badge>
            </div>
          </Popover>

          <Divider type="vertical" className="h-6 mx-2 md:mx-4" />

          <Dropdown
            menu={{ items: userItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div className="cursor-pointer flex items-center py-1 px-1 md:px-2 rounded-full transition-all duration-300">
              <Avatar
                size={32}
                className="bg-blue-500 border-2 border-white flex-shrink-0"
                icon={<UserOutlined />}
                style={{ flexShrink: 0 }}
              />
              {/* Username - hidden on mobile, visible on tablet+ */}
              <Text 
                strong 
                className="ml-2 mr-2 text-gray-800 hidden sm:block truncate"
                style={{ maxWidth: '100px' }}
              >
                {user ? user.username : 'Admin User'}
              </Text>
              <DownOutlined className="text-gray-500 text-xs hidden sm:block" />
            </div>
          </Dropdown>
        </div>
      </Header>

      {/* Logout Confirmation Modal */}
      <Modal
        title="Confirm Logout"
        open={isLogoutModalVisible}
        onOk={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        footer={[
          <Button key="cancel" onClick={handleLogoutCancel}>
            Cancel
          </Button>,
          <Button key="logout" type="primary" danger onClick={handleLogoutConfirm}>
            Logout
          </Button>
        ]}
      >
        <p>Are you sure you want to logout?</p>
      </Modal>

    </ConfigProvider>
  );
};

export default AppHeader;
